import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Typography,
  Button,
  Box,
  Chip,
  Tooltip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DownloadIcon from '@mui/icons-material/Download'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ImageIcon from '@mui/icons-material/Image'
import VideoFileIcon from '@mui/icons-material/VideoFile'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import RestartAltIcon from '@mui/icons-material/RestartAlt'

const DocumentPreviewModal = ({
  open,
  onClose,
  fileUrl,
  fileName,
  fileExt,
  isImage,
  isPdf,
  isVideo,
  onOpenNewTab,
  onDownload,
  onCopyLink,
  originalFileName,
  actions,
}) => {
  const [zoom, setZoom] = useState(1)

  const zoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3))
  const zoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.5))
  const resetZoom = () => setZoom(1)

  const icon = isImage ? (
    <ImageIcon color="primary" />
  ) : isPdf ? (
    <PictureAsPdfIcon color="error" />
  ) : isVideo ? (
    <VideoFileIcon color="primary" />
  ) : (
    <InsertDriveFileIcon color="action" />
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      // The dialog is portalled but React events bubble through the component
      // tree, so without this a click on Close (or the backdrop) reaches
      // whatever clickable element mounted the preview - e.g. a document row
      // whose onClick opens it again.
      onClick={(e) => e.stopPropagation()}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          height: { xs: '92vh', md: '86vh' },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
            {icon}
            <Box minWidth={0}>
              <Typography fontWeight={700} noWrap>
                {originalFileName ? fileName : 'File Viewer'}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  label={fileExt?.toUpperCase() || 'FILE'}
                  variant="outlined"
                />
                <Typography variant="caption" color="text.secondary">
                  Secure temporary preview
                </Typography>
              </Stack>
            </Box>
          </Stack>

          {isImage && (
            <Stack direction="row" alignItems="center">
              <Tooltip title="Zoom out">
                <span>
                  <IconButton onClick={zoomOut} disabled={zoom <= 0.5}>
                    <ZoomOutIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Chip
                size="small"
                label={`${Math.round(zoom * 100)}%`}
                sx={{ alignSelf: 'center' }}
              />

              <Tooltip title="Zoom in">
                <span>
                  <IconButton onClick={zoomIn} disabled={zoom >= 3}>
                    <ZoomInIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Reset zoom">
                <IconButton onClick={resetZoom}>
                  <RestartAltIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          )}

          <Stack direction="row" spacing={0.5}>
            {actions}
            <Tooltip title="Download">
              <IconButton onClick={onDownload}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>

            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          bgcolor: '#F7F9FC',
          height: '100%',
        }}
      >
        {isImage ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              background:
                'radial-gradient(circle at top, #ffffff 0%, #EEF3F8 100%)',
            }}
          >
            <Box
              component="img"
              src={fileUrl}
              alt={fileName}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: 3,
                boxShadow: '0 18px 50px rgba(15, 23, 42, 0.18)',
                bgcolor: 'white',
              }}
            />
          </Box>
        ) : isVideo ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              bgcolor: '#0F172A',
            }}
          >
            <Box
              component="video"
              src={fileUrl}
              controls
              autoPlay
              playsInline
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                borderRadius: 3,
                boxShadow: '0 18px 50px rgba(15, 23, 42, 0.45)',
                outline: 'none',
              }}
            />
          </Box>
        ) : isPdf ? (
          <Box
            component="iframe"
            src={fileUrl}
            title={fileName}
            sx={{
              border: 0,
              width: '100%',
              height: '100%',
              minHeight: 600,
            }}
          />
        ) : (
          <Stack
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100%', p: 4 }}
          >
            <InsertDriveFileIcon
              sx={{ fontSize: 64, color: 'text.secondary' }}
            />
            <Typography variant="h6" fontWeight={700}>
              Preview not available
            </Typography>
            <Typography color="text.secondary" textAlign="center">
              This file type can’t be previewed in AmbuNet yet. You can open or
              download it securely.
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={onDownload}
            >
              Download file
            </Button>
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          This preview link is temporary and will expire automatically.
        </Typography>

        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default DocumentPreviewModal
